import { useEffect, useMemo, useState } from 'react';
import { Key, Users, ChevronRight, Radio, Pencil, Share2, Eye, Headphones, UserPlus } from 'lucide-react';
import SovereignDrawer from './SovereignDrawer';
import { generateRandomAvatar } from './avatarGenerator';
import { CollectionCarousel, ArtistCarousel } from './CrateDiggingCarousel';
import { getFanMusicImpact, setMuteAllNotifications } from '../../services/api';

interface Collection {
  id: number;
  tenantId: number;
  name: string | null;
  coverUrl: string | null;
  issueNumber: number;
  productionLimit: number | null;
  productionType: string | null;
  acquiredAt: number;
}

interface Artist {
  id: number;
  artistName: string;
  slug: string;
  profileUrl: string | null;
  joinedAt: number;
  hasUncollectedPressings?: boolean;
  statusText: string | null;
  latestTransmission: { headline: string; date: number | null } | null;
}

interface FanUser {
  id: number;
  name: string;
  email: string;
  status: string;
  profileImageUrl: string | null;
  userReference: string | null;
}

interface CollectionRoomProps {
  user: FanUser;
  collections: Collection[];
  artists: Artist[];
  allNotificationsMuted: boolean;
  userReference: string;
  onEditProfile?: () => void;
}

interface MusicImpactData {
  shares: number;
  visits: number;
  listens: number;
  newFans: number;
}

export default function CollectionRoom({ user, collections, artists, allNotificationsMuted, userReference, onEditProfile }: CollectionRoomProps) {
  const [collectionsDrawerOpen, setCollectionsDrawerOpen] = useState(false);
  const [artistsDrawerOpen, setArtistsDrawerOpen] = useState(false);
  const [muteAll, setMuteAll] = useState(allNotificationsMuted);
  const [muteLoading, setMuteLoading] = useState(false);
  const [activeArtistId, setActiveArtistId] = useState<number | null>(artists[0]?.id ?? null);
  const [musicImpact, setMusicImpact] = useState<MusicImpactData | null>(null);
  const [musicImpactStatus, setMusicImpactStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  const avatar = user.profileImageUrl || generateRandomAvatar(user.userReference || user.email);

  useEffect(() => {
    if (artists.length === 0) {
      setActiveArtistId(null);
      return;
    }

    if (!artists.some((artist) => artist.id === activeArtistId)) {
      setActiveArtistId(artists[0].id);
    }
  }, [artists, activeArtistId]);

  const activeArtist = useMemo(
    () => artists.find((artist) => artist.id === activeArtistId) ?? null,
    [artists, activeArtistId]
  );

  const activeArtistCollections = useMemo(
    () => activeArtist
      ? collections
        .filter((collection) => collection.tenantId === activeArtist.id)
        .sort((a, b) => b.acquiredAt - a.acquiredAt)
      : [],
    [collections, activeArtist]
  );

  const hasAnyShares = (musicImpact?.shares || 0) > 0;

  const impactMessage = useMemo(() => {
    if (!musicImpact) return null;
    if (musicImpact.newFans > 0) {
      const noun = musicImpact.newFans === 1 ? 'new fan' : 'new fans';
      return `Your shares have introduced ${musicImpact.newFans} ${noun} to independent artists.`;
    }
    if (musicImpact.listens > 0) {
      const noun = musicImpact.listens === 1 ? 'listen' : 'listens';
      return `Your shares have brought ${musicImpact.listens} ${noun} to independent artists.`;
    }
    return null;
  }, [musicImpact]);

  useEffect(() => {
    let cancelled = false;

    const loadMusicImpact = async () => {
      setMusicImpactStatus('loading');
      try {
        const res = await getFanMusicImpact(userReference);
        if (!res.ok) {
          if (!cancelled) {
            setMusicImpactStatus('error');
          }
          return;
        }

        const json = await res.json() as { data?: Partial<MusicImpactData> };
        if (cancelled) return;

        setMusicImpact({
          shares: Number(json.data?.shares || 0),
          visits: Number(json.data?.visits || 0),
          listens: Number(json.data?.listens || 0),
          newFans: Number(json.data?.newFans || 0),
        });
        setMusicImpactStatus('ready');
      } catch {
        if (!cancelled) {
          setMusicImpactStatus('error');
        }
      }
    };

    loadMusicImpact();

    return () => {
      cancelled = true;
    };
  }, [userReference]);

  const handleEnterVault = (collection: Collection) => {
    // Navigate to View Vault page
    window.location.href = `/pressing/${collection.id}`;
  };

  const handleEnterArtist = (artist: { slug: string }) => {
    // Navigate to Artist Public Profile page
    window.location.href = `/artist/${artist.slug}`;
  };

  const handleToggleMuteAll = async () => {
    if (!user.userReference || muteLoading) return;
    setMuteLoading(true);
    try {
      const newMuted = !muteAll;
      const res = await setMuteAllNotifications(user.userReference, newMuted);
      if (res.ok) {
        setMuteAll(newMuted);
      }
    } catch (err) {
      console.error('Failed to toggle mute-all:', err);
    } finally {
      setMuteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-deep-charcoal">
      {/* Header */}
      <header className="sticky top-0 z-40 glass-card border-b border-white/5 rounded-none px-4 sm:px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <img
            src={avatar}
            alt={user.name}
            className="w-12 h-12 rounded-full object-cover border-2 border-white/20"
          />
          <div className="flex-1">
            <h1 className="text-lg font-bold text-white">My Collection</h1>
            <p className="text-sm text-text-secondary">{user.name}</p>
          </div>
          {onEditProfile && (
            <button
              onClick={onEditProfile}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono text-text-muted hover:text-white border border-white/10 hover:border-white/30 rounded-lg transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit Profile
            </button>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-10">
        {/* ─── Followed Artists Carousel ─── */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="terminal-label">followed_artists</span>
            </div>
            {artists.length > 3 && (
              <button
                onClick={() => setArtistsDrawerOpen(true)}
                className="text-xs text-white/70 font-mono hover:text-white transition-colors flex items-center gap-1"
              >
                See All <ChevronRight className="w-3 h-3" />
              </button>
            )}
          </div>

          {artists.length === 0 ? (
            <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-8 text-center">
              <Users className="w-8 h-8 text-text-muted mx-auto mb-3" />
              <p className="text-sm text-text-secondary">No artists followed yet</p>
              <p className="text-xs text-text-muted mt-1">Artists you follow will appear here</p>
            </div>
          ) : (
            <ArtistCarousel
              artists={artists}
              onEnterArtist={handleEnterArtist}
              onActiveArtistChange={(artist) => setActiveArtistId(artist?.id ?? null)}
            />
          )}
        </section>

        {/* ─── Collected Pressings For Center Artist ─── */}
        {activeArtist && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="terminal-label">{activeArtist.artistName.toLowerCase().replace(/\s+/g, '_')}_pressings</span>
              </div>
              {activeArtistCollections.length > 3 && (
                <button
                  onClick={() => setCollectionsDrawerOpen(true)}
                  className="text-xs text-white/70 font-mono hover:text-white transition-colors flex items-center gap-1"
                >
                  See All <ChevronRight className="w-3 h-3" />
                </button>
              )}
            </div>

            {activeArtistCollections.length === 0 ? (
              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-8 text-center">
                <Key className="w-8 h-8 text-text-muted mx-auto mb-3" />
                <p className="text-sm text-text-secondary">No collected pressings for this artist yet</p>
                <p className="text-xs text-text-muted mt-1">Pressings you collect from {activeArtist.artistName} will appear here</p>
              </div>
            ) : (
              <CollectionCarousel
                collections={activeArtistCollections}
                onEnterVault={handleEnterVault}
              />
            )}
          </section>
        )}

        <section className="pt-1">
          <div className="mb-4">
            <h2 className="text-white text-xl font-semibold tracking-tight">Your Music Impact</h2>
            <p className="text-sm text-text-secondary mt-1">The music you've helped others discover</p>
          </div>

          <div className="min-h-[196px]">
            {musicImpactStatus === 'loading' && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 animate-pulse">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="bg-white/5 rounded-xl px-4 py-4 border border-white/10">
                    <div className="h-3 w-16 bg-white/10 rounded" />
                    <div className="h-8 w-14 bg-white/10 rounded mt-3" />
                  </div>
                ))}
              </div>
            )}

            {musicImpactStatus === 'error' && (
              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6 text-center">
                <p className="text-sm text-text-secondary">Your music impact is temporarily unavailable.</p>
              </div>
            )}

            {musicImpactStatus === 'ready' && !hasAnyShares && (
              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6 sm:p-8">
                <p className="text-white text-base sm:text-lg font-medium">Good music deserves to be heard.</p>
                <p className="text-sm text-text-secondary mt-2">Share a track you love and see how many people discover it through you.</p>
                <p className="text-xs text-text-muted mt-3">Your sharing activity will appear here.</p>
              </div>
            )}

            {musicImpactStatus === 'ready' && hasAnyShares && musicImpact && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <div className="bg-white/[0.03] rounded-xl px-4 py-4 border border-white/10">
                    <div className="flex items-center gap-2 text-text-muted text-xs uppercase tracking-wide">
                      <Share2 className="w-3.5 h-3.5 text-electric-cyan/80" />
                      Shares
                    </div>
                    <p className="mt-2 text-3xl font-semibold text-white leading-none">{musicImpact.shares}</p>
                  </div>

                  <div className="bg-white/[0.03] rounded-xl px-4 py-4 border border-white/10">
                    <div className="flex items-center gap-2 text-text-muted text-xs uppercase tracking-wide">
                      <Eye className="w-3.5 h-3.5 text-electric-cyan/80" />
                      Visits
                    </div>
                    <p className="mt-2 text-3xl font-semibold text-white leading-none">{musicImpact.visits}</p>
                  </div>

                  <div className="bg-white/[0.03] rounded-xl px-4 py-4 border border-white/10">
                    <div className="flex items-center gap-2 text-text-muted text-xs uppercase tracking-wide">
                      <Headphones className="w-3.5 h-3.5 text-electric-cyan/80" />
                      Listens
                    </div>
                    <p className="mt-2 text-3xl font-semibold text-white leading-none">{musicImpact.listens}</p>
                  </div>

                  <div className="bg-white/[0.03] rounded-xl px-4 py-4 border border-white/10">
                    <div className="flex items-center gap-2 text-text-muted text-xs uppercase tracking-wide">
                      <UserPlus className="w-3.5 h-3.5 text-electric-cyan/80" />
                      New Fans
                    </div>
                    <p className="mt-2 text-3xl font-semibold text-white leading-none">{musicImpact.newFans}</p>
                  </div>
                </div>

                {impactMessage && (
                  <p className="text-sm text-text-secondary mt-4">{impactMessage}</p>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      {/* Collections Drawer ("The Deep Crate") */}
      <SovereignDrawer
        isOpen={collectionsDrawerOpen}
        onClose={() => setCollectionsDrawerOpen(false)}
        title="The Deep Crate"
      >
        <div className="space-y-3">
          {activeArtistCollections.map((collection) => (
            <div
              key={collection.id}
              onClick={() => { window.location.href = `/pressing/${collection.id}`; }}
              className="flex items-center gap-3 p-3 bg-white/5 rounded-lg border border-white/5 hover:border-white/20 transition-colors cursor-pointer"
            >
              <div className="w-14 h-14 rounded-md overflow-hidden bg-[#1A1A1A] flex-shrink-0 relative">
                {/* Spine effect */}
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-r from-white/20 to-transparent" />
                {collection.coverUrl ? (
                  <img src={collection.coverUrl} alt={collection.name || 'Vault'} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Key className="w-5 h-5 text-text-muted" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{collection.name || 'Untitled Vault'}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Key className="w-3 h-3 text-white/50" />
                  <span className="text-xs text-text-muted font-mono">
                    #{collection.issueNumber}
                    {collection.productionLimit
                      ? ` / ${collection.productionLimit}`
                      : ' UNLIMITED'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </SovereignDrawer>

      {/* Artists Drawer ("The Artist Rolodex") */}
      <SovereignDrawer
        isOpen={artistsDrawerOpen}
        onClose={() => setArtistsDrawerOpen(false)}
        title="Followed Artists"
      >
        <div className="space-y-3">
          {artists.map((artist) => (
            <div
              key={artist.id}
              onClick={() => { window.location.href = `/artist/${artist.slug}`; }}
              className="flex items-center gap-3 p-3 bg-white/5 rounded-lg border border-white/5 hover:border-white/20 transition-colors cursor-pointer"
            >
              <div className="w-12 h-12 rounded-full overflow-hidden bg-[#1A1A1A] flex-shrink-0">
                {artist.profileUrl ? (
                  <img src={artist.profileUrl} alt={artist.artistName} className="w-full h-full object-cover" />
                ) : (
                  <img src={generateRandomAvatar(artist.slug)} alt={artist.artistName} className="w-full h-full object-cover" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{artist.artistName}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] font-mono text-white/50 px-1.5 py-0.5 rounded bg-white/5 border border-white/10">
                    ARTIST-{String(artist.id).padStart(3, '0')}
                  </span>
                </div>
                {artist.statusText && (
                  <div className="flex items-center gap-1 mt-1">
                    <Radio className="w-3 h-3 text-green-400" />
                    <span className="text-[10px] text-green-400/80 truncate">
                      {artist.statusText}
                    </span>
                  </div>
                )}
                {artist.latestTransmission && !artist.statusText && (
                  <div className="flex items-center gap-1 mt-1">
                    <Radio className="w-3 h-3 text-green-400" />
                    <span className="text-[10px] text-text-muted truncate">
                      {artist.latestTransmission.headline}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </SovereignDrawer>

      {/* Mute All Toggle — discrete footer */}
      <footer className="max-w-5xl mx-auto px-4 sm:px-6 py-8 border-t border-white/5">
        <div className="flex items-center gap-2">
          <button
            onClick={handleToggleMuteAll}
            disabled={muteLoading}
            className={`relative w-7 h-3.5 rounded-full transition-colors duration-200 flex-shrink-0 ${
              muteAll ? 'bg-electric-cyan/60' : 'bg-white/10'
            } ${muteLoading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
            aria-label="Mute notifications from all artists"
          >
            <span
              className={`absolute top-0.5 left-0.5 w-2.5 h-2.5 rounded-full bg-white transition-transform duration-200 ${
                muteAll ? 'translate-x-3' : 'translate-x-0'
              }`}
            />
          </button>
          <span className="text-[10px] text-white/30">Mute notifications from all artists</span>
        </div>
      </footer>
    </div>
  );
}
